import { Box, Paper, Text, Group, Badge, Stack, Button, Radio, Checkbox, Flex } from "@mantine/core";
import { IconArrowRight, IconArrowLeft } from "@tabler/icons-react";
import type { KeyboardEvent, CSSProperties } from "react";
import type {
  Question,
  Answer,
  ScaleAnswer,
  PolicyAnswer,
  MultiSelectAnswer,
} from "@/utils/gradingEngine.types";

const BRAND = "var(--mantine-color-primary-6)";
const ROW_BORDER = "var(--mantine-color-primary-3)";
const ROW_SELECTED = "var(--mantine-color-primary-1)";
const SLATE_LINE = "var(--mantine-color-slate-2)";

type Props = {
  question: Question;
  answer: Answer | undefined;
  questionIndex: number;
  totalQuestions: number;
  sectionTitle: string;
  isFirst: boolean;
  isLast: boolean;
  readOnly?: boolean;
  submitting?: boolean;
  canSubmit?: boolean;
  onChange: (answer: Answer) => void;
  onClear: () => void;
  onPrev: () => void;
  onNext: () => void;
  onSubmit: () => void;
};

function optionRowStyle(selected: boolean, interactive: boolean): CSSProperties {
  return {
    borderRadius: 10,
    border: `1px solid ${selected ? ROW_BORDER : SLATE_LINE}`,
    background: selected ? ROW_SELECTED : "#ffffff",
    padding: "12px 14px",
    transition: "background 120ms ease, border-color 120ms ease, box-shadow 120ms ease",
    boxShadow: selected ? "0 0 0 1px rgba(4, 48, 92, 0.06)" : undefined,
    cursor: interactive ? "pointer" : "default",
    outline: "none",
  };
}

function rowKeyActivate(fn: () => void, disabled: boolean) {
  return (e: KeyboardEvent) => {
    if (disabled) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      fn();
    }
  };
}

export default function AssessmentQuestionCard({
  question,
  answer,
  questionIndex,
  totalQuestions,
  sectionTitle,
  isFirst,
  isLast,
  readOnly = false,
  submitting = false,
  canSubmit = false,
  onChange,
  onClear,
  onPrev,
  onNext,
  onSubmit,
}: Props) {
  const hasAnswer =
    question.type === "multi_select"
      ? ((answer as MultiSelectAnswer | undefined)?.selectedOptionIds?.length ?? 0) > 0
      : !!(answer as ScaleAnswer | PolicyAnswer | undefined)?.selectedOptionId;

  const singleSelected =
    question.type === "multi_select"
      ? null
      : ((answer as ScaleAnswer | PolicyAnswer | undefined)?.selectedOptionId ?? null);

  const typeLabel =
    question.type === "scale"
      ? "SCALE"
      : question.type === "policy"
        ? "SINGLE CHOICE"
        : "MULTIPLE CHOICE";

  const handleSingleChange = (optionId: string) => {
    if (readOnly) return;
    const next: ScaleAnswer | PolicyAnswer = { questionId: question.id, selectedOptionId: optionId };
    onChange(next);
  };

  const handleMultiToggle = (optionId: string) => {
    if (readOnly) return;
    const current = (answer as MultiSelectAnswer | undefined)?.selectedOptionIds ?? [];
    const nextIds = current.includes(optionId) ? current.filter((id) => id !== optionId) : [...current, optionId];
    onChange({ questionId: question.id, selectedOptionIds: nextIds } as MultiSelectAnswer);
  };

  return (
    <Paper
      shadow="sm"
      radius="md"
      p={0}
      style={{
        background: "#ffffff",
        border: `1px solid ${SLATE_LINE}`,
        overflow: "hidden",
      }}
    >
      <Box px={{ base: "md", sm: "xl" }} pt={{ base: "md", sm: "lg" }} pb="sm">
        <Text fw={700} fz="md" c="primary.7">
          {sectionTitle}
        </Text>
        <Text fz="xs" c="slate.5" mt={4}>
          Question {questionIndex + 1} of {totalQuestions}
        </Text>
      </Box>

      <Flex
        justify="space-between"
        align="flex-start"
        gap="md"
        wrap="nowrap"
        px={{ base: "md", sm: "xl" }}
        pb="md"
      >
        <Text fw={600} fz="md" c="primary.8" style={{ flex: 1, lineHeight: 1.5 }}>
          {question.text}
          {question.required && (
            <Text component="span" c="yellow.8" ml={6} fw={700}>
              *
            </Text>
          )}
        </Text>
        <Badge
          variant="outline"
          color="slate"
          size="sm"
          radius="sm"
          styles={{ root: { textTransform: "none", borderColor: "var(--mantine-color-slate-3)", color: "var(--mantine-color-slate-6)" } }}
        >
          {typeLabel}
        </Badge>
      </Flex>

      <Box px={{ base: "md", sm: "xl" }} pb="md">
        {(question.type === "scale" || question.type === "policy") && (
          <Radio.Group
            value={(answer as ScaleAnswer | PolicyAnswer | undefined)?.selectedOptionId ?? ""}
            onChange={handleSingleChange}
            role="radiogroup"
            aria-label={question.text}
          >
            <Stack gap={10}>
              {question.options.map((opt) => {
                const selected = singleSelected === opt.id;
                const radioTabStopId = singleSelected ?? question.options[0]?.id;
                return (
                  <Box
                    key={opt.id}
                    role="radio"
                    aria-checked={selected}
                    tabIndex={readOnly ? -1 : opt.id === radioTabStopId ? 0 : -1}
                    style={optionRowStyle(selected, !readOnly)}
                    onClick={readOnly ? undefined : () => handleSingleChange(opt.id)}
                    onKeyDown={rowKeyActivate(() => handleSingleChange(opt.id), readOnly)}
                  >
                    <Radio
                      value={opt.id}
                      label={opt.label}
                      disabled={readOnly}
                      color="primary"
                      tabIndex={-1}
                      styles={{
                        root: { width: "100%", pointerEvents: "none" },
                        label: { width: "100%", fontSize: 14 },
                        radio: { pointerEvents: "none" },
                      }}
                    />
                  </Box>
                );
              })}
            </Stack>
          </Radio.Group>
        )}

        {question.type === "multi_select" && (
          <Stack gap={10} role="group" aria-label={question.text}>
            {question.options.map((opt) => {
              const selected =
                (answer as MultiSelectAnswer | undefined)?.selectedOptionIds?.includes(opt.id) ?? false;
              return (
                <Box
                  key={opt.id}
                  role="checkbox"
                  aria-checked={selected}
                  tabIndex={readOnly ? -1 : 0}
                  style={optionRowStyle(selected, !readOnly)}
                  onClick={readOnly ? undefined : () => handleMultiToggle(opt.id)}
                  onKeyDown={rowKeyActivate(() => handleMultiToggle(opt.id), readOnly)}
                >
                  <Checkbox
                    checked={selected}
                    onChange={() => {}}
                    label={opt.label}
                    disabled={readOnly}
                    color="primary"
                    tabIndex={-1}
                    styles={{
                      root: { width: "100%", pointerEvents: "none" },
                      label: { width: "100%", fontSize: 14 },
                      input: { pointerEvents: "none" },
                      inner: { pointerEvents: "none" },
                    }}
                  />
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>

      <Flex
        justify="space-between"
        align="center"
        gap="md"
        wrap="wrap"
        px={{ base: "md", sm: "xl" }}
        pb={{ base: "md", sm: "lg" }}
        pt="xs"
      >
        <Group gap="sm">
          {!isFirst && (
            <Button
              variant="default"
              size="sm"
              leftSection={<IconArrowLeft size={16} />}
              onClick={onPrev}
              disabled={readOnly}
            >
              Previous
            </Button>
          )}
          {hasAnswer && !readOnly && (
            <Button
              variant="default"
              size="sm"
              onClick={onClear}
              styles={{
                root: {
                  backgroundColor: "var(--mantine-color-slate-1)",
                  borderColor: "var(--mantine-color-slate-2)",
                  color: "var(--mantine-color-slate-7)",
                },
              }}
            >
              Clear
            </Button>
          )}
        </Group>

        {isLast ? (
          <Button
            color="primary"
            size="sm"
            rightSection={<IconArrowRight size={16} />}
            onClick={onSubmit}
            loading={submitting}
            disabled={readOnly || !canSubmit}
            styles={{
              root: {
                backgroundColor: BRAND,
              },
            }}
          >
            Submit Assessment
          </Button>
        ) : (
          <Button
            color="primary"
            size="sm"
            rightSection={<IconArrowRight size={16} />}
            onClick={onNext}
            disabled={readOnly}
            styles={{
              root: {
                backgroundColor: BRAND,
              },
            }}
          >
            Next Question
          </Button>
        )}
      </Flex>
    </Paper>
  );
}
